import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { authApi } from '../../api/auth';
import { useAuth } from '../../lib/auth-context';

export function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [locale, setLocale] = useState('en');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setDisplayName(user.display_name ?? '');
      setTimezone(user.timezone ?? 'UTC');
      setLocale(user.locale ?? 'en');
    }
  }, [user]);

  async function saveProfile(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const updated = await authApi.updateProfile({ display_name: displayName, timezone, locale });
      await refreshUser();
      setMessage(`Profile saved for ${updated.email}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setLoading(false);
    }
  }

  async function changePassword(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await authApi.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setMessage('Password changed successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Password change failed');
    } finally {
      setLoading(false);
    }
  }

  if (!user) {
    return null;
  }

  return (
    <div className="mx-auto max-w-2xl p-6">
      <h1 className="text-2xl font-semibold text-slate-900">Profile</h1>
      <p className="mt-1 text-sm text-slate-600">{user.email}</p>
      {error && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {message && <p className="mt-4 rounded-lg bg-green-50 p-3 text-sm text-green-700">{message}</p>}

      <form onSubmit={saveProfile} className="mt-8 space-y-4 rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="font-medium text-slate-900">Account details</h2>
        <label className="block text-sm">
          Display name
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" />
        </label>
        <label className="block text-sm">
          Timezone
          <input value={timezone} onChange={(e) => setTimezone(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" />
        </label>
        <label className="block text-sm">
          Locale
          <input value={locale} onChange={(e) => setLocale(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" />
        </label>
        <button type="submit" disabled={loading} className="rounded-lg bg-slate-900 px-4 py-2 text-white disabled:opacity-50">
          Save profile
        </button>
      </form>

      <form onSubmit={changePassword} className="mt-6 space-y-4 rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="font-medium text-slate-900">Change password</h2>
        <label className="block text-sm">
          Current password
          <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" />
        </label>
        <label className="block text-sm">
          New password
          <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" />
        </label>
        <button type="submit" disabled={loading} className="rounded-lg bg-slate-900 px-4 py-2 text-white disabled:opacity-50">
          Change password
        </button>
      </form>

      <p className="mt-6 text-sm">
        <Link to="/account/sessions" className="text-blue-600 hover:underline">Manage active sessions</Link>
      </p>
    </div>
  );
}
